/**
 * Day bucketing for the Combined View timeline.
 *
 * The feed groups entries under a date header, and the per-date file strip is
 * indexed by the same key. Both derive it here from the entry's epoch
 * timestamp.
 *
 * Previously the server sent five pre-formatted date strings per row and the
 * client grouped by splitting one of them on the literal " at ". That tied the
 * grouping to the server's locale and to an exact spacing convention, so a
 * change to either silently produced one group per row. Deriving the key from
 * the timestamp means the headers, the buckets and the times rendered inside a
 * card cannot disagree.
 */

/** Milliseconds per second, for the epoch-seconds the API returns. */
const MS = 1000

/**
 * Returns the local calendar day for an epoch-seconds timestamp as YYYY-MM-DD.
 *
 * The key sorts lexicographically in chronological order, which lets the group
 * list keep whatever order the server's sort produced without re-parsing it.
 */
export function combinedViewDateKey(occurredAt) {
  const date = toDate(occurredAt)
  if (!date) return ""
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${date.getFullYear()}-${month}-${day}`
}

/**
 * Formats the time shown on an individual entry, e.g. "8:20 AM".
 */
export function combinedViewTime(occurredAt) {
  const date = toDate(occurredAt)
  if (!date) return ""
  return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
}

/**
 * Formats a day key for a group header, e.g. "09 August 2019".
 */
export function combinedViewDateLabel(dateKey) {
  if (!dateKey) return ""
  const [year, month, day] = dateKey.split("-").map(Number)
  if (!year || !month || !day) return dateKey
  // Constructed as a local date so the label matches the key it came from.
  const date = new Date(year, month - 1, day)
  return date.toLocaleDateString(undefined, { day: "2-digit", month: "long", year: "numeric" })
}

/**
 * Accepts epoch seconds (what the API sends) and tolerates a missing or
 * unparseable value by returning null, so a malformed entry renders without a
 * date rather than crashing the whole feed.
 */
function toDate(occurredAt) {
  const seconds = Number(occurredAt)
  if (!Number.isFinite(seconds) || seconds <= 0) return null
  const date = new Date(seconds * MS)
  return Number.isNaN(date.getTime()) ? null : date
}
